import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUp, ListOrdered, Loader2, RotateCcw, Swords, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, posterUrl } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";

const ROUNDS = 6;
const pairKey = (a, b) => [a, b].sort((x, y) => x - y).join("-");

function Contender({ movie, side, state, onPick }) {
  return (
    <motion.button onClick={onPick} data-testid={`faceoff-card-${side}`} disabled={state !== "idle"}
      initial={{ opacity: 0, x: side === "left" ? -30 : 30 }}
      animate={{ opacity: state === "lost" ? 0.35 : 1, x: 0, scale: state === "won" ? 1.04 : state === "lost" ? 0.95 : 1 }}
      whileHover={state === "idle" ? { y: -6 } : undefined} transition={{ type: "spring", stiffness: 260, damping: 22 }}
      className={`relative aspect-[2/3] w-full rounded-3xl overflow-hidden border-2 bg-card text-left ${state === "won" ? "border-primary shadow-[0_0_40px_hsl(var(--primary)/0.45)]" : "border-transparent"}`}>
      <img src={posterUrl(movie.poster_path, "w500")} alt={movie.title} className="h-full w-full object-cover" draggable={false} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
      <div className="absolute bottom-0 p-4 text-white">
        <p className="font-display font-black text-lg sm:text-2xl leading-tight line-clamp-2">{movie.title}</p>
        <p className="text-xs sm:text-sm text-white/60">{movie.year}</p>
      </div>
    </motion.button>
  );
}

function RankingList({ ranking, prevRanks }) {
  return (
    <div className="rounded-3xl border bg-card p-4" data-testid="faceoff-ranking">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold flex items-center gap-2"><Trophy className="h-3.5 w-3.5" /> Your ranking</p>
        <Link to="/rank" className="inline-flex items-center gap-1 text-xs font-semibold text-primary" data-testid="faceoff-rank-link">
          <ListOrdered className="h-3.5 w-3.5" /> Edit
        </Link>
      </div>
      <ul className="space-y-1.5 max-h-[60vh] overflow-y-auto">
        {ranking.map((m, i) => {
          const prev = prevRanks[m.movie_id];
          const move = prev === undefined ? 0 : prev - i;
          return (
            <motion.li layout key={m.movie_id} transition={{ type: "spring", stiffness: 300, damping: 30 }}
              data-testid={`faceoff-rank-${m.movie_id}`} className="flex items-center gap-3 p-1.5 rounded-xl">
              <span className="w-6 text-center font-display font-black text-muted-foreground">{i + 1}</span>
              <img src={posterUrl(m.poster_path, "w92")} alt="" className="h-10 w-7 rounded object-cover" />
              <span className="text-sm font-semibold truncate flex-1">{m.title}</span>
              {move > 0 && <span className="text-[11px] font-bold text-[hsl(var(--sp-right))] flex items-center"><ArrowUp className="h-3 w-3" />{move}</span>}
              {move < 0 && <span className="text-[11px] font-bold text-[hsl(var(--sp-left))] flex items-center"><ArrowDown className="h-3 w-3" />{-move}</span>}
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}

function Progress({ round }) {
  return (
    <div className="flex items-center gap-1.5" data-testid="faceoff-progress">
      {Array.from({ length: ROUNDS }).map((_, i) => (
        <span key={i} className={`h-1.5 w-8 rounded-full transition-colors duration-300 ${i < round ? "bg-primary" : "bg-secondary"}`} />
      ))}
      <span className="ml-2 text-xs font-mono-sp text-muted-foreground">{Math.min(round + 1, ROUNDS)}/{ROUNDS}</span>
    </div>
  );
}

function useFaceoff() {
  const [pair, setPair] = useState(null);
  const [round, setRound] = useState(0);
  const [ranking, setRanking] = useState([]);
  const [prevRanks, setPrevRanks] = useState({});
  const [likedCount, setLikedCount] = useState(null);
  const [picked, setPicked] = useState(null);
  const [done, setDone] = useState(false);
  const skip = useRef([]);

  const loadPair = useCallback(async () => {
    const { data } = await api.get("/faceoff/pair", { params: { skip: skip.current.join(",") } });
    setLikedCount(data.liked_count);
    setPair(data.pair);
    setPicked(null);
    if (!data.pair && data.exhausted) setDone(true);
  }, []);

  const start = useCallback(() => {
    skip.current = [];
    setRound(0);
    setDone(false);
    loadPair();
  }, [loadPair]);

  useEffect(() => {
    api.get("/faceoff/ranking").then(({ data }) => setRanking(data.ranking));
    start();
  }, [start]);

  const pick = async (winner, loser) => {
    if (picked) return;
    setPicked(winner.movie_id);
    try {
      const { data } = await api.post("/faceoff/vote", { winner_id: winner.movie_id, loser_id: loser.movie_id });
      setPrevRanks(Object.fromEntries(ranking.map((m, i) => [m.movie_id, i])));
      setRanking(data.ranking);
      skip.current.push(pairKey(winner.movie_id, loser.movie_id));
      await new Promise((r) => setTimeout(r, 380));
      const next = round + 1;
      setRound(next);
      if (next >= ROUNDS) { setDone(true); setPair(null); } else await loadPair();
    } catch (e) {
      toast.error(formatApiError(e));
      setPicked(null);
    }
  };
  return { pair, round, ranking, prevRanks, likedCount, picked, done, pick, start };
}

export default function FaceOff() {
  const { pair, round, ranking, prevRanks, likedCount, picked, done, pick, start } = useFaceoff();

  useEffect(() => {
    const onKey = (e) => {
      if (!pair || picked) return;
      if (e.key === "ArrowLeft") pick(pair[0], pair[1]);
      if (e.key === "ArrowRight") pick(pair[1], pair[0]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (likedCount === null) return <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />;
  if (likedCount < 2)
    return (
      <div className="max-w-2xl mx-auto">
        <PageHeader eyebrow="Rank your favorites" title="Face-off" />
        <div className="rounded-3xl border border-dashed p-10 text-center" data-testid="faceoff-not-enough">
          <Swords className="h-10 w-10 mx-auto text-primary mb-4" />
          <p className="font-display text-xl font-bold">You need at least 2 liked movies.</p>
          <p className="text-muted-foreground text-sm mt-2">Swipe up on movies you've seen and loved, then come back to rank them.</p>
          <Link to="/" className="inline-block mt-6 text-primary font-semibold" data-testid="faceoff-back-link">Keep swiping →</Link>
        </div>
      </div>
    );

  const stateFor = (m) => (!picked ? "idle" : picked === m.movie_id ? "won" : "lost");
  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader eyebrow="Rank your favorites" title="Which is better?" sub="Tap the movie you liked more. Six quick rounds per session.">
        <Progress round={round} />
      </PageHeader>
      <div className="grid lg:grid-cols-[1fr_340px] gap-8 items-start">
        <div>
          {done ? (
            <div className="rounded-3xl border bg-card p-10 text-center fade-up" data-testid="faceoff-complete">
              <Trophy className="h-10 w-10 mx-auto text-primary mb-4" />
              <p className="font-display text-2xl font-black">Session complete</p>
              <p className="text-muted-foreground text-sm mt-2">Your ranking has been updated. It also powers the Liked tab in Seen.</p>
              <button onClick={start} data-testid="faceoff-restart-button"
                className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-primary text-primary-foreground font-bold transition-transform hover:scale-105">
                <RotateCcw className="h-4 w-4" /> Another {ROUNDS} rounds
              </button>
            </div>
          ) : pair ? (
            <div key={pairKey(pair[0].movie_id, pair[1].movie_id)} className="relative grid grid-cols-2 gap-3 sm:gap-6 max-w-2xl mx-auto">
              <Contender movie={pair[0]} side="left" state={stateFor(pair[0])} onPick={() => pick(pair[0], pair[1])} />
              <Contender movie={pair[1]} side="right" state={stateFor(pair[1])} onPick={() => pick(pair[1], pair[0])} />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-background border-2 flex items-center justify-center font-display font-black text-sm sm:text-base pointer-events-none">VS</span>
            </div>
          ) : (
            <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
          )}
        </div>
        <RankingList ranking={ranking} prevRanks={prevRanks} />
      </div>
    </div>
  );
}
